import React, { useState } from 'react';
import {
    AppBar,
    Toolbar,
    Container,
    Button,
    IconButton,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemText,
    Box,
    Typography,
    useScrollTrigger,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';

interface HeaderProps {
    scrollToSection: (id: string) => void;
}

export const Header: React.FC<HeaderProps> = ({ scrollToSection }) => {
    const [mobileOpen, setMobileOpen] = useState(false);
    const [activeItem, setActiveItem] = useState<string | null>(null);
    const [activeSection, setActiveSection] = useState<string>('home');
    const trigger = useScrollTrigger({
        disableHysteresis: true,
        threshold: 20,
    });

    const navItems = [
        { label: 'About', id: 'about' },
        { label: 'Courses', id: 'courses' },
        { label: 'Approach', id: 'approach' },
        { label: 'Why Us', id: 'why' },
        { label: 'For Parents', id: 'parents' },
        { label: 'Contact', id: 'contact' },
    ];

    const handleDrawerToggle = () => {
        setMobileOpen(!mobileOpen);
    };

    const handleNavClick = (id: string) => {
        scrollToSection(id);
        setActiveSection(id);
        setMobileOpen(false);
    };

    const drawer = (
        <Box
            sx={{
                width: 280,
                height: '100%',
                background: 'rgba(255, 255, 255, 0.98)',
                backdropFilter: 'blur(24px)',
            }}
        >
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                px: 3,
                py: 3,
                borderBottom: '1px solid rgba(91, 76, 219, 0.15)',
            }}>
                <Typography
                    variant="h6"
                    sx={{
                        fontWeight: 700,
                        color: '#5b4cdb',
                    }}
                >
                    Menu
                </Typography>
                <IconButton
                    onClick={handleDrawerToggle}
                    sx={{
                        color: '#5b4cdb',
                        '&:hover': {
                            background: 'rgba(91, 76, 219, 0.1)',
                            transform: 'rotate(90deg)',
                        },
                        transition: 'all 0.3s ease',
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </Box>
            <List sx={{ px: 2, py: 3 }}>
                {navItems.map((item, index) => (
                    <ListItem
                        key={item.id}
                        disablePadding
                        sx={{
                            mb: 1,
                            animation: `slideIn 0.3s ease ${index * 0.1}s both`,
                            '@keyframes slideIn': {
                                from: {
                                    opacity: 0,
                                    transform: 'translateX(20px)',
                                },
                                to: {
                                    opacity: 1,
                                    transform: 'translateX(0)',
                                },
                            },
                        }}
                    >
                        <ListItemButton
                            onClick={() => handleNavClick(item.id)}
                            sx={{
                                borderRadius: 2,
                                py: 1.5,
                                position: 'relative',
                                overflow: 'hidden',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    left: 0,
                                    top: 0,
                                    bottom: 0,
                                    width: '4px',
                                    background: 'linear-gradient(180deg, #5b4cdb 0%, #06b6d4 100%)',
                                    transform: 'scaleY(0)',
                                    transition: 'transform 0.3s ease',
                                },
                                '&:hover': {
                                    background: 'rgba(0, 0, 0, 0.05)',
                                    '&::before': {
                                        transform: 'scaleY(1)',
                                    },
                                },
                            }}
                        >
                            <ListItemText
                                primary={item.label}
                                primaryTypographyProps={{
                                    sx: {
                                        fontWeight: 600,
                                        fontSize: '1.1rem',
                                        transition: 'all 0.3s ease',
                                    }
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>
        </Box>
    );

    return (
        <>
            <AppBar
                position="fixed"
                elevation={0}
                sx={{
                    background: trigger
                        ? 'rgba(255, 255, 255, 0.98)'
                        : 'rgba(255, 255, 255, 0.95)',
                    backdropFilter: 'blur(12px)',
                    WebkitBackdropFilter: 'blur(12px)',
                    borderBottom: trigger
                        ? '1px solid rgba(91, 76, 219, 0.2)'
                        : '1px solid rgba(91, 76, 219, 0.1)',
                    transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                    boxShadow: trigger
                        ? '0 2px 12px rgba(91, 76, 219, 0.1)'
                        : 'none',
                }}
            >
                <Container maxWidth="lg">
                    <Toolbar disableGutters sx={{ justifyContent: 'space-between', py: 0.5 }}>
                        {/* Logo */}
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    transform: 'scale(1.05)',
                                },
                            }}
                            onClick={() => {
                                scrollToSection('home');
                                setActiveSection('home');
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'relative',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: -4,
                                        background: 'linear-gradient(135deg, #5b4cdb, #06b6d4)',
                                        borderRadius: '50%',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                        filter: 'blur(8px)',
                                    },
                                    '&:hover::before': {
                                        opacity: 0.2,
                                    },
                                }}
                            >
                                <img
                                    src="/logo.png"
                                    alt="Myl Logo"
                                    style={{
                                        width: '50px',
                                        height: '50px',
                                        objectFit: 'contain',
                                        position: 'relative',
                                        zIndex: 1,
                                    }}
                                />
                            </Box>
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    lineHeight: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '1.5rem',
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg, #5b4cdb 0%, #06b6d4 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        letterSpacing: '0.05em',
                                        fontFamily: '"IBM Plex Sans", "Helvetica", sans-serif',
                                    }}
                                >
                                    MYL
                                </Typography>
                                <Typography
                                    sx={{
                                        fontSize: '0.75rem',
                                        fontWeight: 600,
                                        color: '#64748b',
                                        letterSpacing: '0.1em',
                                        fontFamily: '"IBM Plex Sans", "Helvetica", sans-serif',
                                        mt: -0.5,
                                    }}
                                >
                                    MASTER YOUR LEARNING !
                                </Typography>
                            </Box>
                        </Box>

                        {/* Desktop Navigation */}
                        <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 0.25, alignItems: 'center' }}>
                            {navItems.map((item) => (
                                <Button
                                    key={item.id}
                                    onClick={() => handleNavClick(item.id)}
                                    onMouseEnter={() => setActiveItem(item.id)}
                                    onMouseLeave={() => setActiveItem(null)}
                                    sx={{
                                        color: activeSection === item.id ? '#5b4cdb' : '#1e293b',
                                        px: { md: 1.5, lg: 2.5 },
                                        py: 1,
                                        position: 'relative',
                                        fontWeight: activeSection === item.id ? 700 : 600,
                                        fontSize: { md: '0.85rem', lg: '0.95rem' },
                                        overflow: 'hidden',
                                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                        '&::before': {
                                            content: '""',
                                            position: 'absolute',
                                            bottom: 0,
                                            left: '50%',
                                            width: activeSection === item.id ? '80%' : '0%',
                                            height: '2px',
                                            background: 'linear-gradient(90deg, #5b4cdb 0%, #06b6d4 100%)',
                                            transform: 'translateX(-50%)',
                                            transition: 'width 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                                        },
                                        '&::after': {
                                            content: '""',
                                            position: 'absolute',
                                            inset: 0,
                                            background: 'radial-gradient(circle at center, rgba(0, 0, 0, 0.08), transparent 70%)',
                                            opacity: activeSection === item.id ? 0.5 : 0,
                                            transition: 'opacity 0.3s ease',
                                        },
                                        '&:hover': {
                                            color: '#000000',
                                            transform: 'translateY(-2px)',
                                            '&::before': {
                                                width: '80%',
                                            },
                                            '&::after': {
                                                opacity: 1,
                                            },
                                        },
                                    }}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            position: 'relative',
                                            zIndex: 1,
                                            display: 'inline-block',
                                            transition: 'transform 0.3s ease',
                                            transform: activeItem === item.id ? 'scale(1.1)' : 'scale(1)',
                                        }}
                                    >
                                        {item.label}
                                    </Box>
                                </Button>
                            ))}
                        </Box>

                        {/* CTA Button */}
                        <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                            <Button
                                variant="contained"
                                onClick={() => scrollToSection('contact')}
                                sx={{
                                    px: 3,
                                    py: 1.2,
                                    position: 'relative',
                                    overflow: 'hidden',
                                    fontWeight: 700,
                                    fontSize: '0.95rem',
                                    background: 'linear-gradient(135deg, #5b4cdb 0%, #7c6eeb 100%)',
                                    boxShadow: '0 4px 16px rgba(91, 76, 219, 0.3)',
                                    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                    '&::before': {
                                        content: '""',
                                        position: 'absolute',
                                        inset: 0,
                                        background: '#000000',
                                        opacity: 0,
                                        transition: 'opacity 0.3s ease',
                                    },
                                    '&::after': {
                                        content: '""',
                                        position: 'absolute',
                                        top: '50%',
                                        left: '50%',
                                        width: '0',
                                        height: '0',
                                        borderRadius: '50%',
                                        background: 'rgba(255, 255, 255, 0.3)',
                                        transform: 'translate(-50%, -50%)',
                                        transition: 'width 0.6s ease, height 0.6s ease',
                                    },
                                    '&:hover': {
                                        transform: 'translateY(-2px) scale(1.05)',
                                        boxShadow: '0 8px 28px rgba(0, 0, 0, 0.3)',
                                        '&::before': {
                                            opacity: 1,
                                        },
                                        '&::after': {
                                            width: '300px',
                                            height: '300px',
                                        },
                                    },
                                    '&:active': {
                                        transform: 'translateY(0) scale(0.98)',
                                    },
                                }}
                            >
                                <Box component="span" sx={{ position: 'relative', zIndex: 1 }}>
                                    Get Started
                                </Box>
                            </Button>
                        </Box>

                        {/* Mobile Menu Button */}
                        <IconButton
                            color="inherit"
                            aria-label="open drawer"
                            edge="start"
                            onClick={handleDrawerToggle}
                            sx={{
                                display: { md: 'none' },
                                position: 'relative',
                                color: '#5b4cdb',
                                '&::before': {
                                    content: '""',
                                    position: 'absolute',
                                    inset: -4,
                                    background: 'linear-gradient(135deg, #5b4cdb, #06b6d4)',
                                    borderRadius: '50%',
                                    opacity: 0,
                                    transition: 'opacity 0.3s ease',
                                },
                                '&:hover::before': {
                                    opacity: 0.15,
                                },
                            }}
                        >
                            <MenuIcon />
                        </IconButton>
                    </Toolbar>
                </Container>
            </AppBar>

            {/* Mobile Drawer */}
            <Drawer
                anchor="right"
                open={mobileOpen}
                onClose={handleDrawerToggle}
                sx={{
                    display: { xs: 'block', md: 'none' },
                    '& .MuiDrawer-paper': {
                        boxShadow: '-8px 0 32px rgba(0, 0, 0, 0.5)',
                    },
                }}
            >
                {drawer}
            </Drawer>

            {/* Toolbar spacer */}
            <Toolbar />
        </>
    );
};
